<template lang="pug">
FcwSheet.FcwEntitiesMapLegend(
  v-model="isOpen"
  :position
  :width
  :title="t('entities_map__legend__title')"
  @open="emit('open')"
  @close="emit('close')"
)
  template(#content)
    .FcwEntitiesMapLegend__content
      .FcwEntitiesMapLegend__section(v-if="showAreaItems")
        h5 {{ t('entities_map__legend__areas__title') }}
        .FcwEntitiesMapLegend__items
          FcwEntitiesMapLegendItem(
            :title="t('entities_map__legend__areas__service_area__title')"
            :description="t('entities_map__legend__areas__service_area__description')")
            template(#icon)
              FcwServiceAreaIcon.FcwEntitiesMapLegend__itemIcon(
                :color="theme.colorPrimary"
              )
          FcwEntitiesMapLegendItem(
            :title="t('entities_map__legend__areas__forbidden_area__title')"
            :description="t('entities_map__legend__areas__forbidden_area__description')")
            template(#icon)
              FcwForbiddenAreaIcon.FcwEntitiesMapLegend__itemIcon(
                :color="theme.colorNeutralDark3"
              )
      .FcwEntitiesMapLegend__section(v-if="stationItems?.length")
        h5 {{ t('entities_map__legend__stations__title') }}
        .FcwEntitiesMapLegend__items
          FcwEntitiesMapLegendItem(
            v-for="item in stationItems"
            :key="item.title"
            :title="item.title"
            :description="item.description"
            :icon="item.icon"
          )
      .FcwEntitiesMapLegend__section(v-if="pointOfInterestItems?.length")
        h5 {{ t('entities_map__legend__points_of_interest__title') }}
        .FcwEntitiesMapLegend__items
          FcwEntitiesMapLegendItem(
            v-for="item in pointOfInterestItems"
            :key="item.title"
            :title="item.title"
            :description="item.description"
            :icon="item.icon"
          )
</template>

<style lang="stylus">
.FcwEntitiesMapLegend__content
  display flex
  flex-direction column
  gap rem(24)
  padding 0 rem(16)

.FcwEntitiesMapLegend__section
  h5
    margin-bottom rem(12)

.FcwEntitiesMapLegend__items
  display flex
  flex-direction column
  gap rem(12)

.FcwEntitiesMapLegend__item
  display flex
  align-items flex-start
  gap rem(12)

  p
    use-font('body-1')
    color var(--color--neutral--dark-3)
    font-size rem(14)
    line-height rem(16)

.FcwEntitiesMapLegend__itemIcon
  width rem(56)
  flex-shrink 0

.FcwEntitiesMapLegend__itemIconMapMarker
  margin-top rem(-8)

.FcwEntitiesMapLegend__itemText
  h6
    margin-bottom rem(4)
</style>

<script setup lang="ts">
import FcwServiceAreaIcon from './components/FcwServiceAreaIcon.vue';
import FcwForbiddenAreaIcon from './components/FcwForbiddenAreaIcon.vue';
import FcwEntitiesMapLegendItem, {
  type FcwEntitiesMapLegendItemProps,
} from './components/FcwEntitiesMapLegendItem.vue';

export interface FcwEntitiesMapLegendProps {
  /**
   * Whether the legend is open
   */
  modelValue: boolean;
  /**
   * Width of the legend
   */
  width?: number | string;
  /**
   * Position of the legend
   */
  position: 'left' | 'right';
  /**
   * Show area legend items
   */
  showAreaItems?: boolean;
  /**
   * Station legend items
   */
  stationItems?: FcwEntitiesMapLegendItemProps[];
  /**
   * Points of interest legend items
   */
  pointOfInterestItems?: FcwEntitiesMapLegendItemProps[];
}

const props = withDefaults(defineProps<FcwEntitiesMapLegendProps>(), {
  showAreaItems: true,
  stationItems: () => [],
  pointOfInterestItems: () => [],
  width: '408px',
});

const emit = defineEmits<{
  (name: 'open'): void;
  (name: 'close'): void;
}>();

const { theme } = useClientStore().clientState;
const { t } = useClientI18n();

const isOpen = useVModelProxy({ props });
</script>
